/**
 * The total and the pay button, with the price-type selector above them.
 *
 * Two layouts from one component. On a phone (`bar`) it is a collapsed strip that expands into
 * the list on tap — what keeps the four-tap accessory sale to four taps: search, tap the
 * product, tap Charge, tap Cash; the cart never has to be opened. On the two-panel desktop
 * surface (`foot`) it sits at the foot of an always-visible cart, so there is nothing to expand.
 *
 * Charge is the one primary action here, and it is the only thing in the primary accent. The
 * retail/wholesale switch is a mode, not an action, so it sits above the total as a segmented
 * control and never competes with Charge for weight.
 */

import { formatKes } from '@pos/shared';

import { Button, cn } from '@/shared/ui';

export type CartPanelProps = {
  /** Distinct rows — catalogue lines and misc. lines both count as one each. */
  rowCount: number;
  totalKes: number;
  /** Total units across all lines. */
  count: number;
  /** What is in the sale, newest last — named on the phone's collapsed bar. */
  itemNames: readonly string[];
  priceType: 'retail' | 'wholesale';
  canSubmit: boolean;
  /** `bar`: collapsible strip, phone. `foot`: fixed foot of the desktop cart panel. */
  layout?: 'bar' | 'foot';
  expanded?: boolean;
  onToggleExpanded?: () => void;
  onChangePriceType: (type: 'retail' | 'wholesale') => void;
  onCharge: () => void;
  /** Warms the payment chunk, so tapping Charge is instant rather than a download. */
  onWarmPayment: () => void;
};

export function CartPanel({
  rowCount,
  totalKes,
  count,
  itemNames,
  priceType,
  canSubmit,
  layout = 'bar',
  expanded = false,
  onToggleExpanded,
  onChangePriceType,
  onCharge,
  onWarmPayment,
}: CartPanelProps) {
  if (rowCount === 0) return null;

  const items = `${count} ${count === 1 ? 'item' : 'items'}`;
  // The collapsed bar names what is in the sale — the newest item, with the count under it.
  // "2 lines" alone left a tester sure the cart was empty when it was not: the only proof of the
  // Clear case was a number.
  const named = itemNames.at(-1) ?? '';

  return (
    <div
      className={cn(
        'bg-surface-raised',
        // Docked to the foot of the phone screen, full width; on the two-panel surface a card
        // like the lines above it, rather than a white slab that stops halfway down the column.
        layout === 'bar' ? 'border-t border-line-subtle' : 'mx-4 mt-2 rounded-card shadow-sm',
      )}
      data-print="hide"
    >
      {/* Mode, above the money. A segmented control at chip weight — nowhere near Charge. */}
      <div className="px-4 pt-3">
        <div
          className="flex rounded-control bg-surface-sunken p-1"
          role="group"
          aria-label="Price type"
          data-tour="price-type"
        >
          {(['retail', 'wholesale'] as const).map((type) => (
            <button
              key={type}
              type="button"
              aria-pressed={priceType === type}
              onClick={() => {
                onChangePriceType(type);
              }}
              className={cn(
                'min-h-[2.5rem] flex-1 rounded-md text-sm font-semibold capitalize',
                'transition-[background-color,color] duration-fast ease-standard',
                priceType === type
                  ? 'bg-surface-raised text-content-primary shadow-sm'
                  : 'text-content-muted',
              )}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {layout === 'bar' ? (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex w-full items-center gap-3 px-4 py-3 text-left"
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-semibold text-content-primary">
              {expanded ? 'Hide the list' : named}
            </span>
            <span className="text-xs text-content-muted">
              {items}
              {expanded ? '' : ' · tap to see them'}
            </span>
          </span>
          <span className="text-xl font-bold tracking-tight text-content-primary" data-nums="money">
            {formatKes(totalKes)}
          </span>
          <ChevronIcon up={expanded} />
        </button>
      ) : (
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="flex-1 text-sm font-semibold text-content-secondary">{items}</span>
          <span className="text-xl font-bold tracking-tight text-content-primary" data-nums="money">
            {formatKes(totalKes)}
          </span>
        </div>
      )}

      <div className="px-4 pb-3">
        <Button
          size="lg"
          fullWidth
          disabled={!canSubmit}
          data-tour="charge"
          onClick={onCharge}
          onPointerEnter={onWarmPayment}
          onTouchStart={onWarmPayment}
        >
          Charge
        </Button>
      </div>
    </div>
  );
}

function ChevronIcon({ up }: { up: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn(
        'text-content-muted transition-transform duration-fast ease-standard',
        up && 'rotate-180',
      )}
    >
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
